import { api } from './api';

export interface LLDProblem {
 id: string;
 title: string;
 slug: string;
 difficulty: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
 category: 'Object Modeling' | 'System Management' | 'Payment and Splitting' | 'Booking and Reservation' | 'Real-Time Services';
 description: string;
 clarifications: { question: string; answer: string }[];
 expectedEntities: { name: string; description: string; attributes: object[]; methods: object[] }[];
 expectedRelationships: { source: string; target: string; type: string; cardinality: string }[];
 referenceSolution: {
  uml: object;
  code: Record<string, object[]>;
  explanation: string;
 };
 testCases: { id: string; name: string; category: 'functional' | 'edge-case'; input: string; expectedOutput: string; assertion: string }[];
 hints: Record<string, string[]>;
 simulatorConfig: { type: string; initialState: object; scenarios: object[] };
 boilerplateCode: Record<string, object[]>;
 isActive: boolean;
 orderIndex: number;
 createdAt: string;
 updatedAt: string;
}

export interface LLDProblemWithProgress extends LLDProblem {
 progress?: {
  isCompleted: boolean;
  mode: string;
  totalTimeSpent: number;
  lastSavedAt?: string;
 };
}

export interface LLDProblemFilter {
 difficulty?: string;
 category?: string;
}

class LLDProblemService {
 /**
  * Get all LLD problems with optional difficulty/category filters.
  * Public endpoint — no auth required.
  */
 async getProblems(filters: LLDProblemFilter = {}): Promise<LLDProblem[]> {
  const queryParams = new URLSearchParams();

  if (filters.difficulty) {
   queryParams.append('difficulty', filters.difficulty);
  }
  if (filters.category) {
   queryParams.append('category', filters.category);
  }

  const query = queryParams.toString();
  return await api.get(`/lld-practice/problems${query ? `?${query}` : ''}`);
 }

 /**
  * Get full problem details by ID.
  * Public endpoint — no auth required.
  */
 async getProblemById(id: string): Promise<LLDProblem> {
  return await api.get(`/lld-practice/problems/${id}`);
 }

 /**
  * Get problems annotated with the current user's completion status.
  * Requires JWT authentication.
  */
 async getProblemsWithProgress(): Promise<LLDProblemWithProgress[]> {
  return await api.get('/lld-practice/problems/with-progress');
 }
}

export const lldProblemService = new LLDProblemService();
export default lldProblemService;
